// src/hooks/useVisibilityRefresh.ts
import { useEffect } from 'react';
import i18next from 'i18next';
import { modals } from '@mantine/modals';
import { SHELL_NS } from 'recruiter-i18n/namespaces/shell';
import { CORE_NS } from 'recruiter-i18n/namespaces/core';
import env from 'env';
import { isRecord, isString } from 'utils/typeGuards';

// Each visibility change can detect the same new build, so only one reload
// prompt is open at a time. Cleared whenever that prompt closes.
let reloadPromptOpen = false;

/**
 * Asks to reload into the new build. The static `modals` API reaches the
 * ModalsProvider that App renders, which is mounted by the time a visibility
 * change fires.
 *
 * Title fills the header. Without one, the close button sits in a blank bar
 * over a single line of body text.
 */
function promptReload(): void {
  if (reloadPromptOpen) return;
  reloadPromptOpen = true;
  modals.openConfirmModal({
    title: i18next.t('appUpdate.title', { ns: SHELL_NS }),
    size: 'sm',
    centered: true,
    children: i18next.t('appUpdate.body', { ns: SHELL_NS }),
    labels: {
      confirm: i18next.t('errors.reloadPage', { ns: CORE_NS }),
      cancel: i18next.t('actions.cancel', { ns: CORE_NS }),
    },
    onConfirm: () => {
      window.location.reload();
    },
    onClose: () => {
      reloadPromptOpen = false;
    },
  });
}

/**
 * When the tab becomes visible, fetch /version.json once.
 * If it doesn’t match the built APP_VERSION, prompt to reload.
 * vite-plugins/appVersion.ts writes both from one value per build.
 */
export function useVisibilityRefresh(): void {
  useEffect(() => {
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      try {
        if (env.IS_LOCAL) {
          return;
        }
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const body: unknown = await res.json();
        // A malformed version.json says nothing about a new build.
        if (!isRecord(body) || !isString(body.version)) return;
        const { version } = body;
        // eslint-disable-next-line no-console
        console.debug('version', version);
        // eslint-disable-next-line no-console
        console.debug('APP_VERSION', import.meta.env.APP_VERSION);
        if (version !== import.meta.env.APP_VERSION) {
          promptReload();
        }
      } catch {
        // swallow-ok: version.json is only an update hint; an unavailable/malformed build version leaves the current application usable and the next visible return checks again.
        // ignore network/json errors
      }
    };

    const handleVisibilityChange = () => {
      void onVisibilityChange();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
